/* ==========================================================================
   FarmDaddy storefront behaviour  ·  fd.js
   Vanilla ES2017, no dependencies, ~9 KB. Everything is progressive enhancement:
   every control works as a normal link/form without this file.
   Hooks are data-fd-* attributes. Config comes from window.FD (set in theme.liquid).
   Modules: nav · search · cart (drawer + AJAX add + cart page) · video facade · quantity steppers · collection filters
   ========================================================================== */
(function () {
  'use strict';

  var FD = window.FD || {};
  var routes = FD.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add.js', cartChange: '/cart/change.js', predictive: '/search/suggest.json', search: '/search' };
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };
  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Polite live region for screen-reader announcements ---------------------- */
  var live = document.createElement('div');
  live.className = 'fd-vh';
  live.setAttribute('role', 'status');
  live.setAttribute('aria-live', 'polite');
  document.addEventListener('DOMContentLoaded', function () { document.body.appendChild(live); });
  function announce(msg) { live.textContent = ''; setTimeout(function () { live.textContent = msg; }, 30); }

  /* Scroll lock + focus trap ------------------------------------------------- */
  var lockCount = 0;
  function lockScroll() { if (lockCount++ === 0) document.body.classList.add('fd-scroll-lock'); }
  function unlockScroll() { if (--lockCount <= 0) { lockCount = 0; document.body.classList.remove('fd-scroll-lock'); } }

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    var items = $$(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* NAV ----------------------------------------------------------------------- */
  function initNav() {
    var mnav = $('[data-fd-mnav]');
    var opener = $('[data-fd-mnav-open]');
    if (mnav && opener) {
      var open = function () {
        mnav.classList.add('is-open'); opener.setAttribute('aria-expanded', 'true'); lockScroll();
        var close = $('[data-fd-mnav-close]', mnav); if (close) close.focus();
      };
      var close = function () {
        if (!mnav.classList.contains('is-open')) return;
        mnav.classList.remove('is-open'); opener.setAttribute('aria-expanded', 'false'); unlockScroll(); opener.focus();
      };
      opener.addEventListener('click', open);
      $$('[data-fd-mnav-close]', mnav).forEach(function (b) { b.addEventListener('click', close); });
      mnav.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); else trapFocus(mnav, e); });
      window.matchMedia('(min-width: 900px)').addEventListener('change', function (m) { if (m.matches) close(); });
    }

    // Desktop dropdowns: label navigates, caret toggles (touch + keyboard); hover/focus handled in CSS
    var items = $$('[data-fd-nav-item]');
    function closeAll(except) {
      items.forEach(function (it) {
        if (it === except) return;
        it.classList.remove('is-open');
        var t = $('[data-fd-nav-toggle]', it); if (t) t.setAttribute('aria-expanded', 'false');
      });
    }
    items.forEach(function (it) {
      var toggle = $('[data-fd-nav-toggle]', it);
      if (!toggle) return;
      toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var openNow = !it.classList.contains('is-open');
        closeAll(it); it.classList.toggle('is-open', openNow); toggle.setAttribute('aria-expanded', String(openNow));
      });
    });
    document.addEventListener('click', function () { closeAll(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(); });
  }

  /* MODAL helper: a plain <div role="dialog" aria-modal="true" hidden>. Esc and click-outside close it, focus is
     trapped while open and restored afterwards, page scroll is locked. Shared by search and the image lightbox. */
  function makeModal(el, opts) {
    var last;
    function close() {
      if (el.hidden) return;
      el.hidden = true; unlockScroll();
      if (last && last.focus) last.focus();
      if (opts && opts.onClose) opts.onClose();
    }
    function open() {
      last = document.activeElement; el.hidden = false; lockScroll();
      var f = opts && opts.focus ? opts.focus() : null;
      if (!f) { var first = $(FOCUSABLE, el); if (first) first.focus(); }
    }
    el.addEventListener('keydown', function (e) { if (e.key === 'Escape') { e.stopPropagation(); close(); } else trapFocus(el, e); });
    el.addEventListener('click', function (e) { if (e.target === el) close(); });
    return { open: open, close: close };
  }
  FD.modal = makeModal;
  window.FD = FD;

  /* SEARCH (modal + predictive search API) ---------------------------------- */
  function initSearch() {
    var dlg = $('[data-fd-search]');
    if (!dlg) return;   // without it the search form still works as a normal link to /search
    var input = $('[data-fd-search-input]', dlg);
    var results = $('[data-fd-search-results]', dlg);
    var hint = results.innerHTML;
    var timer, ctrl;
    var modal = makeModal(dlg, { focus: function () { input.focus(); return input; }, onClose: function () { input.value = ''; results.innerHTML = hint; } });

    $$('[data-fd-search-open]').forEach(function (b) { b.addEventListener('click', modal.open); });
    $$('[data-fd-search-close]', dlg).forEach(function (b) { b.addEventListener('click', modal.close); });

    var fmt;
    try { fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: FD.currency || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }); } catch (err) { fmt = null; }
    function money(v) { var n = parseFloat(v); return fmt && !isNaN(n) ? fmt.format(n) : v; }

    function render(q, products) {
      results.textContent = '';
      if (!products.length) {
        var p = document.createElement('p'); p.className = 'fd-search__hint'; p.textContent = 'No products found for “' + q + '”.'; results.appendChild(p);
      }
      products.forEach(function (pr) {
        var a = document.createElement('a'); a.className = 'fd-search__item'; a.href = pr.url;
        if (pr.image) { var img = document.createElement('img'); img.src = pr.image; img.alt = ''; img.width = 56; img.height = 56; a.appendChild(img); }
        var box = document.createElement('div');
        var t = document.createElement('strong'); t.textContent = pr.title;
        var pr2 = document.createElement('span'); pr2.textContent = money(pr.price);
        box.appendChild(t); box.appendChild(pr2); a.appendChild(box); results.appendChild(a);
      });
      var all = document.createElement('a'); all.className = 'fd-search__all'; all.href = routes.search + '?q=' + encodeURIComponent(q) + '&type=product';
      all.textContent = 'See all results for “' + q + '”'; results.appendChild(all);
      announce(products.length + ' products found');
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 2) { results.innerHTML = hint; return; }
      timer = setTimeout(function () {
        if (ctrl) ctrl.abort();
        ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        fetch(routes.predictive + '?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last', { signal: ctrl && ctrl.signal, headers: { Accept: 'application/json' } })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
          .then(function (d) { render(q, (d.resources && d.resources.results && d.resources.results.products) || []); })
          .catch(function (err) { if (err.name !== 'AbortError') results.innerHTML = hint; });
      }, 200);
    });
  }

  /* CART ---------------------------------------------------------------------- */
  var drawer, drawerInner, lastFocus;
  function setCount(n) {
    $$('[data-fd-cart-count]').forEach(function (el) {
      el.setAttribute('data-empty', n > 0 ? 'false' : 'true');
      var c = $('[data-cart-count]', el); if (c) c.textContent = n;
    });
  }
  function refreshDrawer() {
    if (!drawer) return Promise.resolve();
    return fetch(routes.root + '?sections=fd-cart-drawer', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var doc = new DOMParser().parseFromString(data['fd-cart-drawer'], 'text/html');
        var fresh = $('[data-fd-drawer-inner]', doc);
        if (fresh) { drawerInner.innerHTML = fresh.innerHTML; setCount(parseInt(fresh.getAttribute('data-count'), 10) || 0); }
      });
  }
  function openDrawer() {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open'); lockScroll();
    var panel = $('.fd-drawer__panel', drawer); if (panel) panel.focus({ preventScroll: true });
  }
  function closeDrawer() {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open'); unlockScroll();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  function changeLine(line, qty, refocus) {
    return fetch(routes.cartChange, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line: line, quantity: qty }) })
      .then(function (r) { if (!r.ok) throw new Error('change'); return r.json(); })
      .then(refreshDrawer)
      .then(function () {
        // The drawer markup is replaced on every change; put keyboard focus back where the user was working
        var el = refocus && $(refocus, drawerInner);
        (el || $('.fd-drawer__panel', drawer)).focus({ preventScroll: true });
      });
  }
  function addToCart(form, btn) {
    var label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); btn.textContent = 'Adding…'; }
    var restore = function (text, delay) { setTimeout(function () { if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.textContent = label; } }, delay || 0); if (btn && text) btn.textContent = text; };
    return fetch(routes.cartAdd, { method: 'POST', body: new FormData(form), headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' } })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok) throw new Error(j.description || j.message || 'Could not add to cart'); return j; }); })
      .then(function () { return refreshDrawer(); })
      .then(function () { restore('Added ✓', 1200); announce('Added to cart'); openDrawer(); })
      .catch(function (err) { restore('Unavailable', 2200); announce(err.message); });
  }

  function initCart() {
    drawer = $('[data-fd-drawer]');
    if (drawer) drawerInner = $('[data-fd-drawer-inner]', drawer);
    var ajax = FD.ajaxCart !== false && !!drawer;

    document.addEventListener('submit', function (e) {
      var form = e.target.closest && e.target.closest('[data-fd-add], [data-fd-product-form]');
      if (!form || !ajax) return;
      e.preventDefault();
      addToCart(form, $('[type="submit"]', form) || e.submitter);
    });

    document.addEventListener('click', function (e) {
      var t = e.target;
      var cartLink = t.closest('[data-fd-cart-open]');
      if (cartLink && ajax && location.pathname.replace(/\/$/, '') !== routes.cart) { e.preventDefault(); refreshDrawer().then(openDrawer, openDrawer); return; }
      if (t.closest('[data-fd-drawer-close]')) { closeDrawer(); return; }
      var qtyBtn = t.closest('[data-fd-drawer] [data-fd-qty]');
      var rm = t.closest('[data-fd-drawer] [data-fd-remove]');
      if (qtyBtn || rm) {
        var lineEl = t.closest('[data-fd-line]'), input = $('[data-fd-qty-input]', lineEl);
        var next = rm ? 0 : Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(qtyBtn.getAttribute('data-fd-qty'), 10));
        lineEl.style.opacity = '.5';
        var lineNo = parseInt(lineEl.getAttribute('data-fd-line'), 10);
        var refocus = rm ? null : '[data-fd-line="' + lineNo + '"] [data-fd-qty="' + qtyBtn.getAttribute('data-fd-qty') + '"]';
        changeLine(lineNo, next, refocus).catch(function () { lineEl.style.opacity = ''; announce('Could not update the cart'); });
      }
    });
    document.addEventListener('change', function (e) {
      var input = e.target.closest && e.target.closest('[data-fd-drawer] [data-fd-qty-input]');
      if (!input) return;
      var lineEl = input.closest('[data-fd-line]');
      var lineNo2 = parseInt(lineEl.getAttribute('data-fd-line'), 10);
      changeLine(lineNo2, Math.max(0, parseInt(input.value, 10) || 0), '[data-fd-line="' + lineNo2 + '"] [data-fd-qty-input]');
    });
    document.addEventListener('keydown', function (e) {
      if (!drawer || !drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeDrawer(); else trapFocus($('.fd-drawer__panel', drawer), e);
    });
  }

  /* VIDEO FACADE: load the YouTube iframe only on click (keeps the page light) */
  function initVideo() {
    $$('[data-fd-video]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-fd-video');
        var f = document.createElement('iframe');
        f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0';
        f.title = btn.getAttribute('data-title') || 'Video'; f.allow = 'autoplay; encrypted-media; picture-in-picture'; f.allowFullscreen = true;
        f.setAttribute('loading', 'lazy');
        btn.replaceWith(f); f.focus();
      });
    });
  }

  /* QUANTITY STEPPERS outside the cart (product page, homepage kit): [data-fd-pqty] buttons + [data-fd-pqty-input] */
  function initQty() {
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-fd-pqty]');
      if (!b) return;
      var input = $('[data-fd-pqty-input]', b.closest('.fd-qty'));
      if (input) input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(b.getAttribute('data-fd-pqty'), 10));
    });
  }

  /* COLLECTION: apply sort/filters without a full page reload (falls back to normal navigation) */
  function initCollection() {
    var root = $('[data-fd-collection]');
    if (!root || !window.fetch || !window.DOMParser) return;
    var results = $('[data-fd-results]', root);
    function load(url, push) {
      results.style.opacity = '.45';
      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var next = $('[data-fd-results]', new DOMParser().parseFromString(html, 'text/html'));
          if (!next) { location.href = url; return; }
          results.innerHTML = next.innerHTML; results.style.opacity = '';
          if (push) history.pushState({}, '', url);
          var c = $('[data-fd-count]', results); announce(c ? c.textContent : 'Results updated');
        })
        .catch(function () { location.href = url; });
    }
    root.addEventListener('change', function (e) {
      var form = e.target.closest('form[data-fd-filters]');
      if (!form) return;
      var params = new URLSearchParams(new FormData(form));
      Array.from(params.keys()).forEach(function (k) { if (params.get(k) === '') params.delete(k); });
      load(location.pathname + '?' + params.toString(), true);
    });
    // Pagination + active-filter links inside the results also update in place
    results.addEventListener('click', function (e) {
      var a = e.target.closest('a[data-fd-ajax]');
      if (!a) return;
      e.preventDefault(); load(a.href, true); results.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    });
    window.addEventListener('popstate', function () { location.reload(); });
  }

  /* CART PAGE: +/- steppers submit the form so totals are recalculated server-side */
  function initCartPage() {
    var form = $('[data-fd-cart-page]');
    if (!form) return;
    form.addEventListener('click', function (e) {
      var b = e.target.closest('[data-fd-qty]');
      if (!b) return;
      var input = $('[data-fd-qty-input]', b.closest('.fd-qty'));
      input.value = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(b.getAttribute('data-fd-qty'), 10));
      form.requestSubmit($('[name="update"]', form));
    });
    form.addEventListener('change', function (e) { if (e.target.matches('[data-fd-qty-input]')) form.requestSubmit($('[name="update"]', form)); });
  }

  function init() { initNav(); initSearch(); initCart(); initVideo(); initQty(); initCollection(); initCartPage(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
